import { TimeFormat } from "../types";
import { timeToMinutes } from "./timeParsing";
import { formatTimeString } from "./timeFormatting";
import { TIME_CONSTANTS, TIME_FORMAT_PLACEHOLDERS } from "../constants";

/**
 * Generates time options for the dropdown based on step, min/max constraints, and format.
 *
 * @param step - Time increment in minutes (default 30)
 * @param min - Minimum allowed time as string
 * @param max - Maximum allowed time as string
 * @param format - Time format (12 or 24 hour)
 * @returns Array of time options formatted as strings
 */
export function generateTimeOptions({
  step = 30,
  min,
  max,
  format,
}: {
  step?: number;
  min?: string;
  max?: string;
  format: TimeFormat;
}): string[] {
  const options: string[] = [];

  const minMinutes = min ? timeToMinutes(min) : 0;
  const maxMinutes = max
    ? timeToMinutes(max)
    : TIME_CONSTANTS.MINUTES_IN_DAY - 1;

  // Generate options at step intervals
  for (
    let minutes = 0;
    minutes < TIME_CONSTANTS.MINUTES_IN_DAY;
    minutes += step
  ) {
    // Skip if outside min/max range
    if (minutes < minMinutes || minutes > maxMinutes) {
      continue;
    }

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    options.push(formatTimeString(hours, mins, format));
  }

  return options;
}

/**
 * Normalizes a time string by removing special characters and converting to lowercase.
 * Used for fuzzy matching in dropdown filtering.
 *
 * @param str - The time string to normalize
 * @returns Normalized string for comparison
 */
function normalizeTimeString(str: string): string {
  return str
    .replace(/[–_:]/g, "") // Remove en dashes, underscores, and colons
    .replace(/\s+/g, "") // Remove spaces
    .toLowerCase();
}

/**
 * Filters time options based on user input.
 *
 * @param options - Array of available time options
 * @param userInput - The current user input (after Maskito processing)
 * @returns Filtered array of matching options
 */
export function filterTimeOptions(
  options: string[],
  userInput: string,
): string[] {
  if (
    !userInput ||
    userInput === TIME_FORMAT_PLACEHOLDERS[12] ||
    userInput === TIME_FORMAT_PLACEHOLDERS[24]
  ) {
    return options;
  }

  const normalizedInput = normalizeTimeString(userInput);

  const filtered = options.filter((option) => {
    const normalizedOption = normalizeTimeString(option);
    return normalizedOption.startsWith(normalizedInput);
  });

  return filtered;
}
